import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get quickAddSource() {
    return this.dataset.quickAddSource || '';
  }

  get productPageUrl() {
    const directProductUrl = this.dataset.productUrl;
    if (directProductUrl) {
      const directUrl = new URL(directProductUrl, window.location.origin);

      if (directUrl.searchParams.has('variant')) {
        return directUrl.toString();
      }

      const selectedVariantId = this.#getSelectedVariantId();
      if (selectedVariantId) {
        directUrl.searchParams.set('variant', selectedVariantId);
      }

      return directUrl.toString();
    }

    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Mounts the boutique quick-add panel that is pre-rendered in the collection card.
   * This avoids fetching and re-hydrating a second product page just to open the modal.
   * @returns {boolean}
   */
  #mountLocalBoutiquePanel() {
    if (this.quickAddSource !== 'boutique') return false;

    const stash = this.querySelector('[data-ea-boutique-panel-stash]');
    const panel = stash?.firstElementChild;
    const dialogComponent = document.getElementById('quick-add-dialog');

    if (!(stash instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(dialogComponent instanceof QuickAddDialog)) {
      return false;
    }

    this.#applyQuickAddContext(dialogComponent);
    dialogComponent.mountBoutiquePanel(stash, panel);
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    return true;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#mountLocalBoutiquePanel()) {
      return;
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const modalSource = this.#extractModalSource(html);
        if (modalSource) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (modalSource.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      if (this.quickAddSource !== 'boutique') {
        this.#updateVariantPicker(productGrid);
      }
      this.#openQuickAddModal();
      return;
    }

    if (currentUrl) {
      window.location.assign(currentUrl);
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#applyQuickAddContext(dialogComponent);
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    const requestUrl = new URL(productPageUrl, window.location.origin);
    if (requestUrl.origin !== window.location.origin) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }
      if (new URL(response.url).origin !== window.location.origin) return null;

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const dialogComponent = document.getElementById('quick-add-dialog');

    if (!productGrid || !modalContent) return;
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.releaseMountedBoutiquePanel();
    }

    productGrid.dataset.quickAddSource = this.quickAddSource;
    if (this.quickAddSource === 'boutique') {
      morph(modalContent, productGrid);
      this.#hydrateScripts(modalContent);
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productHeader = this.#buildProductHeader();
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * @param {Document} html
   * @returns {Element | null}
   */
  #extractModalSource(html) {
    if (this.quickAddSource === 'boutique') {
      return this.#extractBoutiquePanel(html);
    }

    return html.querySelector('[data-product-grid-content]');
  }

  /**
   * @param {Document} html
   * @returns {Element | null}
   */
  #extractBoutiquePanel(html) {
    const panelRoot = html.querySelector('[data-ea-pip]');
    if (!(panelRoot instanceof HTMLElement)) return null;
    const initScript = panelRoot.nextElementSibling;
    if (
      !(initScript instanceof HTMLScriptElement) ||
      !initScript.hasAttribute('data-ea-pip-init') ||
      initScript.hasAttribute('src') ||
      !['', 'text/javascript', 'application/javascript'].includes(initScript.type)
    ) return null;

    const panelWrapper = document.createElement('div');
    panelWrapper.classList.add('quick-add-modal__panel', 'quick-add-modal__panel--boutique');
    panelWrapper.dataset.eaBoutiquePanel = 'true';
    panelWrapper.dataset.quickAddSource = 'boutique';
    const panelClone = /** @type {HTMLElement} */ (panelRoot.cloneNode(true));
    panelClone.dataset.eaSyncUrl = 'false';
    panelWrapper.appendChild(panelClone);
    panelWrapper.appendChild(initScript.cloneNode(true));

    return panelWrapper;
  }

  /**
   * @param {Element} root
   */
  #hydrateScripts(root) {
    const initializers = Array.from(root.children).filter(
      (child) => child instanceof HTMLScriptElement && child.hasAttribute('data-ea-pip-init')
    );
    if (initializers.length !== 1) return;
    const script = initializers[0];
    if (script.hasAttribute('src') || !['', 'text/javascript', 'application/javascript'].includes(script.type)) return;
    const hydratedScript = document.createElement('script');
    hydratedScript.setAttribute('data-ea-pip-init', '');
    if (script.type) hydratedScript.type = script.type;
    if (script.textContent) hydratedScript.textContent = script.textContent;
    script.replaceWith(hydratedScript);
  }

  #buildBoutiqueSummary() {
    const eyebrowText = this.dataset.productEyebrow?.trim();
    if (!eyebrowText) return null;

    const summary = document.createElement('div');
    summary.classList.add('quick-add-modal__summary', 'quick-add-modal__summary--boutique');

    const eyebrow = document.createElement('p');
    eyebrow.classList.add('quick-add-modal__eyebrow');
    eyebrow.textContent = eyebrowText;
    summary.appendChild(eyebrow);

    return summary;
  }

  #buildProductHeader() {
    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');

    if (this.quickAddSource === 'boutique') {
      productHeader.classList.add('product-header--boutique');
      const boutiqueSummary = this.#buildBoutiqueSummary();
      if (boutiqueSummary) {
        productHeader.appendChild(boutiqueSummary);
      }
    }

    const productTitle = document.createElement('a');
    productTitle.textContent = this.dataset.productTitle || '';
    productTitle.href = this.productPageUrl;
    productHeader.appendChild(productTitle);

    return productHeader;
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   */
  #applyQuickAddContext(dialogComponent) {
    const quickAddSource = this.quickAddSource;
    const dialog = dialogComponent.querySelector('.quick-add-modal');
    const modalContent = dialogComponent.querySelector('#quick-add-modal-content');

    const applyOrRemove = (element) => {
      if (!(element instanceof HTMLElement)) return;

      if (quickAddSource) {
        element.dataset.quickAddSource = quickAddSource;
      } else {
        delete element.dataset.quickAddSource;
      }
    };

    applyOrRemove(dialogComponent);
    applyOrRemove(dialog);
    applyOrRemove(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {{stash: HTMLElement, panel: HTMLElement} | null} */
  #mountedBoutiquePanel = null;

  #setHeaderLock(active) {
    document.documentElement.toggleAttribute('data-ea-quick-add-open', active);
    document.dispatchEvent(
      new CustomEvent(active ? 'ea:quick-add-open' : 'ea:quick-add-close', {
        bubbles: false,
      })
    );
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('keydown', this.#handleNestedEscape, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#setHeaderLock(false);
    this.releaseMountedBoutiquePanel();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('keydown', this.#handleNestedEscape, { capture: true });
  }

  showDialog() {
    if (this.refs.dialog?.open) return;

    this.#setHeaderLock(true);
    super.showDialog();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * @param {HTMLElement} stash
   * @param {HTMLElement} panel
   */
  mountBoutiquePanel(stash, panel) {
    this.releaseMountedBoutiquePanel();

    const modalContent = this.querySelector('#quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    this.#mountedBoutiquePanel = { stash, panel };
    panel.dataset.eaBoutiqueActivePanel = 'true';
    modalContent.replaceChildren(panel);
    modalContent.scrollTop = 0;
    this.refs.dialog.scrollTop = 0;
  }

  releaseMountedBoutiquePanel() {
    if (!this.#mountedBoutiquePanel) return;

    const { stash, panel } = this.#mountedBoutiquePanel;
    panel.querySelectorAll('[data-ea-pip]').forEach((root) => {
      root.dispatchEvent(new Event('ea:pip-release'));
    });
    if (panel.matches('[data-ea-pip]')) panel.dispatchEvent(new Event('ea:pip-release'));
    delete panel.dataset.eaBoutiqueActivePanel;

    if (stash.isConnected) {
      stash.appendChild(panel);
    }

    this.#mountedBoutiquePanel = null;
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleNestedEscape = (event) => {
    if (event.key !== 'Escape') return;
    const guideClose = this.querySelector('[data-ea-size-guide-modal]:not([hidden]) [data-ea-size-guide-close]');
    const sizeToggle = this.querySelector('[data-ea-size-toggle][aria-expanded="true"]');
    const control = guideClose || sizeToggle;
    if (!(control instanceof HTMLElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    control.click();
    if (!guideClose) control.focus();
  };

  #handleDialogClose = () => {
    this.#setHeaderLock(false);
    this.releaseMountedBoutiquePanel();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

class AcquisitionGallery extends HTMLElement {
  #events;
  #resize;
  #stopDrag;

  connectedCallback() {
    this.#events = new AbortController();
    const viewport = this.querySelector('[data-ea-acquisition-viewport]');
    const slides = Array.from(this.querySelectorAll('[data-ea-acquisition-slide]'));
    if (!(viewport instanceof HTMLElement) || !slides.length) return;
    this.dataset.eaSlideCount = String(slides.length);
    const buttons = Array.from(this.querySelectorAll('[data-ea-gallery-index]'));
    const counter = this.querySelector('[data-ea-gallery-count]');
    const index = () => {
      const bounds = viewport.getBoundingClientRect();
      const distances = slides.map(slide => Math.abs(slide.getBoundingClientRect().left - bounds.left));
      return distances.indexOf(Math.min(...distances));
    };
    const update = () => {
      if (!viewport.clientWidth) return;
      const current = index();
      const position = `${current + 1} / ${slides.length}`;
      if (counter && counter.textContent !== position) counter.textContent = position;
      buttons.forEach(button => {
        if (Number(button.dataset.eaGalleryIndex) === current) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    };
    const goTo = (target) => {
      const next = Math.max(0, Math.min(slides.length - 1, target));
      viewport.scrollBy({
        left: slides[next].getBoundingClientRect().left - viewport.getBoundingClientRect().left,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    };
    let drag;
    const finishDrag = (event) => {
      if (!drag || (event && event.pointerId !== drag.pointerId)) return;
      const finished = drag;
      drag = undefined;
      const distance = finished.x - (event?.type === 'pointerup' ? event.clientX : finished.lastX);
      const direction = getComputedStyle(viewport).direction === 'rtl' ? -1 : 1;
      const target = event?.type === 'pointerup'
        ? finished.index + (Math.abs(distance) >= Math.min(64, viewport.clientWidth * 0.15) ? Math.sign(distance) * direction : 0)
        : index();
      this.classList.remove('is-dragging');
      if (viewport.hasPointerCapture(finished.pointerId)) viewport.releasePointerCapture(finished.pointerId);
      if (event) goTo(target);
    };
    this.#stopDrag = () => finishDrag();
    viewport.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || !event.isPrimary || event.button !== 0 || slides.length < 2 || drag) return;
      drag = { pointerId: event.pointerId, x: event.clientX, lastX: event.clientX, left: viewport.scrollLeft, index: index() };
      event.preventDefault();
      this.classList.add('is-dragging');
      viewport.setPointerCapture(event.pointerId);
    }, { signal: this.#events.signal });
    viewport.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (!(event.buttons & 1)) {
        finishDrag(event);
        return;
      }
      drag.lastX = event.clientX;
      viewport.scrollTo({ left: drag.left + drag.x - event.clientX, behavior: 'instant' });
    }, { signal: this.#events.signal });
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      viewport.addEventListener(type, finishDrag, { signal: this.#events.signal });
    }
    viewport.addEventListener('dragstart', event => event.preventDefault(), { signal: this.#events.signal });
    buttons.forEach(button => button.addEventListener('click', () => goTo(Number(button.dataset.eaGalleryIndex)), { signal: this.#events.signal }));
    viewport.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = getComputedStyle(viewport).direction === 'rtl' ? -1 : 1;
      goTo(index() + (event.key === 'ArrowRight' ? 1 : -1) * direction);
    }, { signal: this.#events.signal });
    viewport.addEventListener('scroll', update, { passive: true, signal: this.#events.signal });
    this.#resize = new ResizeObserver(update);
    this.#resize.observe(viewport);
    update();
  }

  disconnectedCallback() {
    this.#stopDrag?.();
    this.#stopDrag = undefined;
    this.#events?.abort();
    this.#resize?.disconnect();
  }
}

if (!customElements.get('ea-acquisition-gallery')) {
  customElements.define('ea-acquisition-gallery', AcquisitionGallery);
}
